// src/components/strumenti/CadutaTensione.js
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { leggiNumero } from "../../utils/numeriStrumenti";

const inp = { padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

// Resistività a 20°C, valori di riferimento comunemente usati nella pratica elettrotecnica italiana.
import { resistivitaA } from "../../utils/calcoliStrumenti";

export default function CadutaTensione() {
  const [tipo, setTipo] = useState("trifase");
  const [materiale, setMateriale] = useState("rame");
  const [corrente, setCorrente] = useState("");
  const [lunghezza, setLunghezza] = useState("");
  const [sezione, setSezione] = useState("");
  const [cosphi, setCosphi] = useState("0.9");
  const [tensione, setTensione] = useState("400");
  const [tCond, setTCond] = useState("20"); // W442: temperatura del conduttore (20 °C = come prima)

  const I = leggiNumero(corrente)||0;
  const L = leggiNumero(lunghezza)||0;
  const S = leggiNumero(sezione)||0;
  const cf = leggiNumero(cosphi)||1;
  const V = leggiNumero(tensione)||1;
  const b = tipo==="trifase" ? Math.sqrt(3) : 2;
  const rho = resistivitaA(materiale, Number(tCond)); // ρ(T) = ρ20·(1 + α·(T − 20))

  const puoCalcolare = I>0 && L>0 && S>0;
  const deltaV = puoCalcolare ? (b * rho * L * I * cf) / S : null;
  const deltaVPerc = deltaV!==null ? (deltaV/V)*100 : null;

  const azzera = () => { setCorrente(""); setLunghezza(""); setSezione(""); };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
        <div>
          <label style={lbl}>Tipo linea</label>
          <select value={tipo} onChange={e=>setTipo(e.target.value)} style={inp}>
            <option value="trifase">Trifase</option>
            <option value="monofase">Monofase</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Materiale conduttore</label>
          <select value={materiale} onChange={e=>setMateriale(e.target.value)} style={inp}>
            <option value="rame">Rame</option>
            <option value="alluminio">Alluminio</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom:12 }}>
        <label style={lbl}>Temperatura del conduttore</label>
        <select value={tCond} onChange={e=>setTCond(e.target.value)} style={inp}>
          <option value="20">20 °C (resistività di riferimento)</option>
          <option value="70">70 °C (cavo PVC a pieno carico)</option>
          <option value="90">90 °C (cavo XLPE/EPR a pieno carico)</option>
        </select>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
        <div>
          <label style={lbl}>Corrente (A)</label>
          <input value={corrente} onChange={e=>setCorrente(e.target.value)} style={inp} placeholder="Es. 32"/>
        </div>
        <div>
          <label style={lbl}>Lunghezza linea (m)</label>
          <input value={lunghezza} onChange={e=>setLunghezza(e.target.value)} style={inp} placeholder="Es. 50"/>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:16 }}>
        <div>
          <label style={lbl}>Sezione cavo (mm²)</label>
          <input value={sezione} onChange={e=>setSezione(e.target.value)} style={inp} placeholder="Es. 6"/>
        </div>
        <div>
          <label style={lbl}>cos φ</label>
          <input value={cosphi} onChange={e=>setCosphi(e.target.value)} style={inp}/>
        </div>
        <div>
          <label style={lbl}>Tensione nom. (V)</label>
          <input value={tensione} onChange={e=>setTensione(e.target.value)} style={inp}/>
        </div>
      </div>

      {(cf > 1 || cf <= 0) && <div role="alert" style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"7px 10px", marginBottom:10, fontSize:11, color:"#991b1b" }}>Il cos φ deve essere tra 0 e 1: controlla il valore scritto.</div>}
      <div style={{ background: deltaVPerc>4 ? "#fef2f2" : "#faf5ff", border:`1px solid ${deltaVPerc>4?"#fecaca":"#e9d5ff"}`, borderRadius:10, padding:"14px 16px", textAlign:"center" }}>
        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginBottom:4 }}>Caduta di tensione</div>
        <div style={{ fontSize:22, fontWeight:800, color: deltaVPerc>4 ? "#dc2626" : "#7c3aed" }}>
          {deltaV===null ? "—" : `${deltaV.toLocaleString("it-IT",{maximumFractionDigits:2})} V`}
          {deltaVPerc!==null && <span style={{ fontSize:14, marginLeft:8, color:"#9ca3af" }}>({deltaVPerc.toLocaleString("it-IT",{maximumFractionDigits:2})}%)</span>}
        </div>
        {deltaVPerc!==null && deltaVPerc>4 && (
          <div style={{ fontSize:11, color:"#991b1b", marginTop:6 }}>Supera il 4%, valore comunemente preso come riferimento indicativo — verificare secondo il caso specifico</div>
        )}
      </div>
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>ΔV = k · ρ · L · I · cos φ / S (k = √3 trifase, 2 monofase), ρ del {materiale} a {tCond} °C = {rho.toLocaleString("it-IT",{maximumFractionDigits:5})} Ω·mm²/m. Solo resistenza (reattanza trascurata). Calcolo semplificato, non sostituisce una verifica progettuale.</div>

      <button onClick={()=>{azzera();setCosphi("0.9");setTensione("400");setTipo("trifase");setMateriale("rame");setTCond("20");}}
        style={{ marginTop:12, display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
        <RefreshCw size={12}/> Azzera
      </button>
    </div>
  );
}
